/**
 * Bound Auth0 SDK calls that can hang forever.
 *
 * KORA-66: the Auth0 SDK's worker bridge (`sendMessage` in
 * `@auth0/auth0-spa-js`'s `worker.utils.ts`) has no timer and no
 * `worker.onerror` handler. If the worker never loads or never replies,
 * `initialize()` / `handleRedirectCallback()` never settle — no timeout, no
 * rejection, nothing. `withTimeout` gives every caller a hard upper bound
 * without touching the SDK.
 */
/** Default hard bound (ms) on Auth0 initialize()/handleRedirectCallback(). */
export const AUTH_INIT_TIMEOUT_MS = 10_000;
/** Structured code carried by the error surfaced when the bound expires. */
export const AUTH_INIT_TIMEOUT_CODE = 'auth_init_timeout';
/** Build the error surfaced when a bounded operation exceeds its budget. */
export function createAuthTimeoutError(operation, timeoutMs) {
    const err = new Error(`${operation} timed out after ${timeoutMs}ms`);
    err.code = AUTH_INIT_TIMEOUT_CODE;
    err.operation = operation;
    err.timeoutMs = timeoutMs;
    return err;
}
/**
 * True only for errors produced by createAuthTimeoutError. Matches on the
 * structured `code` field only — never on `err.message` (same rule
 * `EXPECTED_LOGGED_OUT_ERROR_CODES` follows in useAuth0.ts after MM-331/AU2).
 *
 * Narrows only to the `code` field actually checked at runtime, not the full
 * `AuthTimeoutError` shape — narrowing to `AuthTimeoutError` would let a
 * caller trust `.operation`/`.timeoutMs` exist on any object that merely
 * happens to carry a matching `code`, which this check never verifies
 * (GitHub Copilot review of PR #50).
 */
export function isAuthInitTimeoutError(err) {
    return (typeof err === 'object' &&
        err !== null &&
        'code' in err &&
        err.code === AUTH_INIT_TIMEOUT_CODE);
}
/**
 * Reject with an AuthTimeoutError if `promise` has not settled within `timeoutMs`.
 * Does NOT cancel `promise` — the caller decides what a late settle means.
 * `timeoutMs <= 0` returns `promise` unchanged (test escape hatch).
 */
export function withTimeout(promise, timeoutMs, operation) {
    if (timeoutMs <= 0)
        return promise;
    return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
            reject(createAuthTimeoutError(operation, timeoutMs));
        }, timeoutMs);
        promise.then(value => {
            clearTimeout(timer);
            resolve(value);
        }, err => {
            clearTimeout(timer);
            reject(err);
        });
    });
}
